import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import api, { mediaUrl } from "../lib/api";

export default function Blog() {
    const [posts, setPosts] = useState(null);

    const load = useCallback(() => {
        api.get("/blog").then((r) => setPosts(r.data)).catch(() => setPosts([]));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const fmt = (d) =>
        new Date(d + "T00:00:00").toLocaleDateString(undefined, {
            month: "long",
            day: "numeric",
            year: "numeric",
        });

    let content;
    if (posts === null) {
        content = <p className="mt-10 text-sm text-amber-100/50">Loading…</p>;
    } else if (posts.length === 0) {
        content = (
            <p
                data-testid="blog-empty"
                className="mt-10 rounded-2xl border border-dashed border-amber-500/25 bg-[#1F1510]/50 p-8 text-center font-serif text-lg italic text-amber-200/70"
            >
                No notes published yet — more to come soon.
            </p>
        );
    } else {
        content = (
            <div className="mt-10 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
                {posts.map((p, i) => (
                    <motion.div
                        key={p.id}
                        data-testid="blog-card"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.05 }}
                    >
                        <Link
                            to={`/blog/${p.slug}`}
                            className="group block overflow-hidden rounded-2xl border border-amber-500/15 bg-[#1F1510]/50 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-amber-400/50"
                        >
                            {p.cover_image_url ? (
                                <div className="h-44 overflow-hidden">
                                    <img
                                        src={mediaUrl(p.cover_image_url)}
                                        alt={p.title}
                                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                </div>
                            ) : (
                                <div className="h-44 bg-gradient-to-br from-[#281D17] to-[#150e08]" />
                            )}
                            <div className="p-6">
                                <p className="text-xs uppercase tracking-[0.2em] text-amber-400/80">
                                    {fmt(p.date)}
                                </p>
                                <h3 className="mt-2 font-serif text-xl text-amber-100">
                                    {p.title}
                                </h3>
                                {p.excerpt && (
                                    <p className="mt-2 text-sm leading-relaxed text-amber-100/60">
                                        {p.excerpt}
                                    </p>
                                )}
                                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-300">
                                    Read note
                                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </span>
                            </div>
                        </Link>
                    </motion.div>
                ))}
            </div>
        );
    }

    return (
        <div data-testid="blog-page" className="pb-28">
            <PageHeader
                testid="blog-header"
                breadcrumb="Blog"
                title="Notes from the room"
                number="✎"
                label="From the Practice"
                heading="On play, feelings, and family life"
            >
                {content}
            </PageHeader>
        </div>
    );
}
